import { DeleteButton, EditButton, ShowButton } from "@refinedev/antd";
import { Table, Space, Input, Tag, Card } from "antd";
import { SearchOutlined, PlusOutlined, TagOutlined } from "@ant-design/icons";
import { useList, useMany } from "@refinedev/core";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/ui";

export const PriceListList = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<any[]>([]);
    const navigate = useNavigate();

    // Fetch prices data using useList
    const { data, isLoading } = useList({
        resource: "price",
        meta: {
            fields: [
                "id",
                "inventoryItemId",
                "basePrice",
                "discountType",
                "discountValue",
                "minimumQuantity",
                "maximumQuantity",
                "effectivePrice",
                "startDate",
                "endDate",
                "isActive",
                "currencyCode",
                "createdAt",
                "updatedAt",
                {
                    inventoryItem: [
                        "id",
                        "itemCode",
                        "itemName",
                        "itemTypeCode",
                        "isActive",
                        "tax.rate"
                    ]
                }
            ]
        },
    });

    // Get all inventory items to display their names
    const { data: inventoryData } = useMany({
        resource: "inventory",
        ids: data?.data?.map((item: any) => item.inventoryItemId) || [],
        queryOptions: {
            enabled: !!data?.data?.length,
        },
        meta: {
            fields: [
                "id",
                "itemCode",
                "itemName",
                "itemTypeCode",
                "isActive",
                "stockLevel.currentStockLevel",
                "stockLevel.availableStock",
                "tax.rate"
            ]
        }
    });

    // Create a mapping of inventory IDs to names
    const inventoryMap = inventoryData?.data?.reduce((acc: Record<number, any>, item: any) => {
        acc[item.id] = item;
        return acc;
    }, {}) || {};

    // Filter and sort prices when search text changes
    useEffect(() => {
        if (!data?.data) return;

        let prices = data.data as any[];

        // Apply search filter
        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            prices = prices.filter((price: any) => {
                const itemName = price.inventoryItem?.itemName || inventoryMap[price.inventoryItemId]?.itemName || "";
                const itemCode = price.inventoryItem?.itemCode || inventoryMap[price.inventoryItemId]?.itemCode || "";
                const itemType = price.inventoryItem?.itemTypeCode || inventoryMap[price.inventoryItemId]?.itemTypeCode || "";

                return itemName.toLowerCase().includes(searchLower) ||
                    itemCode.toLowerCase().includes(searchLower) ||
                    itemType.toLowerCase().includes(searchLower) ||
                    price.currencyCode?.toLowerCase().includes(searchLower);
            });
        }

        // Sort by createdAt descending (newest first)
        const sorted = [...prices].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setFilteredItems(sorted);
    }, [data, searchText, inventoryMap]);

    const columns = [
        {
            title: "Item",
            key: "item",
            render: (_: any, record: any) => {
                const itemName = record.inventoryItem?.itemName || inventoryMap[record.inventoryItemId]?.itemName;
                return (
                    <span className="font-medium text-text-primary">
                        {itemName || `Item ID: ${record.inventoryItemId}`}
                    </span>
                );
            },
            sorter: (a: any, b: any) => {
                const aName = a.inventoryItem?.itemName || inventoryMap[a.inventoryItemId]?.itemName || "";
                const bName = b.inventoryItem?.itemName || inventoryMap[b.inventoryItemId]?.itemName || "";
                return aName.localeCompare(bName);
            },
        },
        {
            title: "Item Code",
            key: "itemCode",
            render: (_: any, record: any) => {
                const itemCode = record.inventoryItem?.itemCode || inventoryMap[record.inventoryItemId]?.itemCode;
                return <span className="text-text-secondary">{itemCode || "-"}</span>;
            },
            sorter: (a: any, b: any) => {
                const aCode = a.inventoryItem?.itemCode || inventoryMap[a.inventoryItemId]?.itemCode || "";
                const bCode = b.inventoryItem?.itemCode || inventoryMap[b.inventoryItemId]?.itemCode || "";
                return aCode.localeCompare(bCode);
            },
        },
        {
            title: "Type",
            key: "type",
            render: (_: any, record: any) => {
                const itemType = record.inventoryItem?.itemTypeCode || inventoryMap[record.inventoryItemId]?.itemTypeCode;
                return <span className="text-text-secondary">{itemType || "-"}</span>;
            },
        },
        {
            title: "Base Price",
            dataIndex: "basePrice",
            key: "basePrice",
            render: (value: number, record: any) => (
                <span className="font-medium">
                    {record.currencyCode} {parseFloat(value?.toString() || "0").toFixed(2)}
                </span>
            ),
            sorter: (a: any, b: any) => (a.basePrice || 0) - (b.basePrice || 0),
        },
        {
            title: "Effective Price",
            dataIndex: "effectivePrice",
            key: "effectivePrice",
            render: (value: number, record: any) => (
                <span className="font-semibold text-primary">
                    {record.currencyCode} {parseFloat(value?.toString() || "0").toFixed(2)}
                </span>
            ),
            sorter: (a: any, b: any) => (a.effectivePrice || 0) - (b.effectivePrice || 0),
        },
        {
            title: "Created At",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    }) : "-"}
                </span>
            ),
            sorter: (a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            defaultSortOrder: "descend" as const,
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "status",
            render: (value: boolean) => (
                <Tag color={value ? "success" : "default"} className="status-tag">
                    {value ? "Active" : "Inactive"}
                </Tag>
            ),
            sorter: (a: any, b: any) => (a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1),
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_: any, record: any) => (
                <Space size="small">
                    <EditButton hideText size="small" recordItemId={record.id} />
                    <ShowButton hideText size="small" recordItemId={record.id} />
                    <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Prices"
                icon={<TagOutlined />}
                breadcrumbs={[
                    { title: "Prices" },
                ]}
                actions={[
                    {
                        label: "Create",
                        onClick: () => navigate("/prices/create"),
                        type: "primary",
                        icon: <PlusOutlined />,
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by item name, code, type, or currency"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        }}
                        className="custom-table"
                        scroll={{ x: 1200 }}
                    />
                </Card>
            </div>
        </div>
    );
};
